import React, { useEffect, useState } from "react"
import { Alert, Button } from "antd"
import { useHistory, useLocation } from "react-router-dom"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faUserPlus } from "@fortawesome/pro-regular-svg-icons"
import { swimminglyApi } from "../utils"

interface WebsitesInvitationSummary {
  teamMemberId: number
  accessId: number
  orgName: string
  workspaceName: string
}

interface WebsitesInvitesResponse {
  invitations: WebsitesInvitationSummary[]
}

export default function WebsitesInvitesAlert(): JSX.Element | null {
  const [loading, setLoading] = useState<boolean>(false)
  const [invite, setInvite] = useState<WebsitesInvitationSummary | null>(null)
  const history = useHistory()
  const location = useLocation()

  useEffect(() => {
    let mounted = true

    async function loadInvites() {
      setLoading(true)
      try {
        const resp = await swimminglyApi.get("/api/websites/access/my-invitations")
        const payload: WebsitesInvitesResponse =
          (resp as any)?.data && !Array.isArray(resp)
            ? (resp as any).data
            : (resp as any)

        const first = payload && Array.isArray(payload.invitations)
          ? payload.invitations[0] || null
          : null

        if (mounted) {
          setInvite(first)
        }
      } catch {
        if (mounted) {
          setInvite(null)
        }
      } finally {
        if (mounted) {
          setLoading(false)
        }
      }
    }

    const handleInvitesChanged = () => {
      // Re-load invitations whenever another part of the app signals
      // that website workspace invitations may have changed (e.g. on
      // accept/decline from the dashboard).
      loadInvites()
    }

    loadInvites()
    window.addEventListener("websitesInvitesChanged", handleInvitesChanged)

    return () => {
      mounted = false
      window.removeEventListener("websitesInvitesChanged", handleInvitesChanged)
    }
  }, [])

  if (loading || !invite) {
    return null
  }

  // Only show on authenticated app routes
  if (!location.pathname.startsWith("/app")) {
    return null
  }

  // Hide the banner on any Websites dashboard screen. From there, the user
  // can access the Manage Invitations tab directly, so a global "Go to Invite"
  // prompt would be redundant.
  if (location.pathname.startsWith("/app/websites/dashboard")) {
    return null
  }

  const message = `Invite to join the ${invite.orgName} websites workspace`

  return (
    <div style={{ marginBottom: 12 }}>
      <Alert
        banner
        showIcon
        icon={<FontAwesomeIcon icon={faUserPlus} />}
        type="info"
        title={message}
        action={
          <Button
            size="small"
            type="primary"
            onClick={() => {
              history.push("/app/websites/dashboard?tab=invitations")
            }}
          >
            Go to Invite
          </Button>
        }
        closable={false}
      />
    </div>
  )
}
